"use client";

import React, { useState, useRef } from "react";
import {
  Upload,
  FileAudio,
  Sparkles,
  CheckCircle,
  Download,
  AlertCircle,
  RefreshCw,
  X,
  Copy,
} from "lucide-react";
import { Select } from "@/components/FormElements/select";
import { audioLanguage } from "@/app/(app)/(private)/video-subtitles/UploadFlow/data";

const UploadSection = () => {
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [isConverted, setIsConverted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const fileInputRef = useRef<any>(null);
  const [transcriptionText, setTranscriptionText] = useState<string | null>(null);
  const [transcriptionData, setTranscriptionData] = useState<any[] | null>(null);
  const [sourceLanguage, setSourceLanguage] = useState<string>("English (UK)");

  const handleDragOver = (e: any) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = (file: any) => {
    const validAudioTypes = ["audio/mpeg", "audio/wav", "audio/aac", "audio/ogg", "audio/mp3", "audio/x-m4a", "audio/webm"];
    const validExtensions = [".mp3", ".wav", ".aac", ".ogg", ".m4a", ".webm", ".flac"];

    const fileName = file.name?.toLowerCase() || "";
    const fileExt = fileName.substring(fileName.lastIndexOf("."));

    const isValidMime = file.type.startsWith("audio/") || validAudioTypes.includes(file.type);
    const isValidExt = validExtensions.includes(fileExt);

    if (file && (isValidMime || isValidExt)) {
      setUploadedFile(file);
      setIsConverted(false);
      setError(null);
      setShowErrorPopup(false);
      setTranscriptionText(null);
      setTranscriptionData(null);
    } else {
      alert("Invalid file type. Please upload a valid audio file (MP3, WAV, AAC, OGG, M4A, WEBM, FLAC).");
    }
  };

  const handleFileInputChange = (e: any) => {
    if (e.target.files.length > 0) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleConvert = async () => {
    setIsConverting(true);
    setIsUploading(true);
    setIsProcessing(false);
    setUploadProgress(0);
    setError(null);
    setShowErrorPopup(false);

    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("source_language", sourceLanguage);

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);

          if (percentComplete === 100) {
            setIsUploading(false);
            setIsProcessing(true);
          }
        }
      };

      // Handle the response
      const response = await new Promise<Response>((resolve, reject) => {
        xhr.timeout = 300000; // 5 minutes
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // Create a Response object from the XMLHttpRequest
            const response = new Response(xhr.responseText, {
              status: xhr.status,
              statusText: xhr.statusText,
              headers: new Headers({
                'Content-Type': xhr.getResponseHeader('Content-Type') || 'application/json'
              })
            });
            resolve(response);
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error occurred'));
        xhr.ontimeout = () => reject(new Error('Request timed out'));

        xhr.open('POST', process.env.NEXT_PUBLIC_UPLOAD_BACKEND_API_URL + "tools/please/transcribe");
        xhr.send(formData);
      });

      if (!response.ok) {
        let errorMessage = "Transcription failed. Please try again.";

        switch (response.status) {
          case 422:
            errorMessage = "Invalid file format or corrupted file. Please check your audio file.";
            break;
          case 500:
            errorMessage = "Server error occurred. Please try again later.";
            break;
          case 413:
            errorMessage = "File too large. Please upload a smaller audio file.";
            break;
          case 429:
            errorMessage = "Too many requests. Please wait a moment and try again.";
            break;
          default:
            errorMessage = `Error ${response.status}: ${response.statusText || "Unknown error occurred"}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Handle array response format (save as-is for later processing)
      if (Array.isArray(data)) {
        if (data.length === 0) {
          throw new Error("No transcription output received from server. Please try again.");
        }
        // Save the array as-is
        setTranscriptionData(data);
        // Log the transcription data
        console.log("Transcription Data:", data);
        // Also create a combined text version for display
        const combinedText = data.map((segment: any) => segment.title || "").filter(Boolean).join(" ");
        setTranscriptionText(combinedText);
      } else if (data.text || data.transcription) {
        // Handle old format with text or transcription field
        const transcription = data.text || data.transcription || "";
        if (!transcription) {
          throw new Error("No transcription output received from server. Please try again.");
        }
        setTranscriptionText(transcription);
        setTranscriptionData(null);
      } else {
        throw new Error("No transcription output received from server. Please try again.");
      }
      setIsConverting(false);
      setIsUploading(false);
      setIsProcessing(false);
      setIsConverted(true);
      setUploadProgress(0);
    } catch (error: any) {
      console.error("API Error:", error);
      setIsConverting(false);
      setIsUploading(false);
      setIsProcessing(false);
      setUploadProgress(0);
      setError(error.message || "An unexpected error occurred. Please try again.");
      setShowErrorPopup(true);
    }
  };

  const handleRetry = () => {
    setError(null);
    setShowErrorPopup(false);
    handleConvert();
  };

  const formatTime = (timeString: string) => {
    // Format time like "0 - 2.5599999427795" to "00:00 - 00:02"
    const parts = timeString.split(" - ");
    if (parts.length === 2) {
      const formatSeconds = (seconds: string) => {
        const sec = parseFloat(seconds);
        const mins = Math.floor(sec / 60);
        const secs = Math.floor(sec % 60);
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      };
      return `${formatSeconds(parts[0])} - ${formatSeconds(parts[1])}`;
    }
    return timeString;
  };

  const handleCopyText = () => {
    if (transcriptionData && transcriptionData.length > 0) {
      const textToCopy = transcriptionData
        .map((segment: any) => `[${formatTime(segment.time)}] ${segment.title}`)
        .join('\n');
      navigator.clipboard.writeText(textToCopy);
    } else if (transcriptionText) {
      navigator.clipboard.writeText(transcriptionText);
    }
  };

  const handleDownloadText = () => {
    let textToDownload = "";
    if (transcriptionData && transcriptionData.length > 0) {
      textToDownload = transcriptionData
        .map((segment: any) => `[${formatTime(segment.time)}] ${segment.title}`)
        .join('\n');
    } else if (transcriptionText) {
      textToDownload = transcriptionText;
    }
    
    if (textToDownload) {
      const blob = new Blob([textToDownload], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${uploadedFile?.name?.replace(/\.[^/.]+$/, '') || 'transcription'}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const closeErrorPopup = () => {
    setShowErrorPopup(false);
  };

  const formatFileSize = (bytes: any) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
      <div className="max-w-4xl mt-4 mx-auto relative">
        {/* Error Popup */}
        {showErrorPopup && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
                <button
                    onClick={closeErrorPopup}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>

                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>

                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Transcription Failed
                  </h3>

                  <p className="text-gray-600 mb-6">
                    {error}
                  </p>

                  <div className="flex gap-3">
                    <button
                        onClick={closeErrorPopup}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                        onClick={handleRetry}
                        className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}

        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 p-4">
          {!uploadedFile ? (
              <div
                  className={`border-3 border-dashed rounded-2xl p-12 text-center transition-all duration-300 ${
                      isDragOver
                          ? "border-purple-500 bg-purple-50 scale-105"
                          : "border-gray-300 hover:border-purple-400 hover:bg-gray-50"
                  }`}
              >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*,.mp3,.wav,.aac,.ogg,.m4a,.webm,.flac"
                    onChange={handleFileInputChange}
                    className="hidden"
                />

                <div className="">
                  <div
                      className="mx-auto w-18 h-18 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
                    <Upload className="w-10 h-10 text-white"/>
                  </div>
                </div>

                <h3 className="text-2xl font-semibold text-gray-800">
                  Drop your audio file here
                </h3>
                <p className="text-gray-600 mb-3">
                  or click to browse from your device
                </p>

                <div className="flex flex-wrap justify-center gap-3 mb-6">
                  {["MP3", "WAV", "AAC", "OGG", "M4A", "WEBM"].map((format) => (
                      <span
                          key={format}
                          className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                      >
                  {format}
                </span>
                  ))}
                </div>

                {/* Language Selection integrated with upload */}
                <div className="max-w-md mx-auto mb-6" onClick={(e) => e.stopPropagation()}>
                  <Select
                    label="Select Audio Language"
                    items={audioLanguage}
                    name="source_language"
                    value={sourceLanguage}
                    defaultValue="English (UK)"
                    onChange={(e) => setSourceLanguage(e.target.value)}
                    required
                    className="mb-0"
                  />
                </div>

                <button
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg">
                  Choose Audio File
                </button>

                <p className="mt-5 w-full flex flex-col items-center text-center text-sm text-yellow-700 dark:text-yellow-400 gap-2">
                  <span className="flex items-center justify-center gap-2">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6"
                    >
                      <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z"
                      />
                    </svg>
                    <span className="font-medium">Files must not exceed 1 GB in size</span>
                  </span>
                </p>

              </div>
          ) : (
              <div className="space-y-6">
                {/* File Info with Language Selection */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div
                          className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center">
                        <FileAudio className="w-6 h-6 text-white"/>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">
                          {uploadedFile.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {formatFileSize(uploadedFile.size)}
                        </p>
                      </div>
                    </div>
                    <button
                        onClick={() => {
                          setUploadedFile(null);
                          setIsConverted(false);
                          setIsConverting(false);
                          setIsUploading(false);
                          setIsProcessing(false);
                          setUploadProgress(0);
                          setError(null);
                          setShowErrorPopup(false);
                          setTranscriptionText(null);
                          setTranscriptionData(null);
                        }}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Language Selection integrated with file info */}
                  <div className="mb-4">
                    <Select
                      label="Select Audio Language"
                      items={audioLanguage}
                      name="source_language"
                      value={sourceLanguage}
                      defaultValue="English (UK)"
                      onChange={(e) => setSourceLanguage(e.target.value)}
                      required
                      className="mb-0"
                    />
                  </div>

                  {/* Show error state inline */}
                  {error && !showErrorPopup && (
                      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                        <div className="flex items-center gap-3 text-red-700 mb-3">
                          <AlertCircle className="w-5 h-5" />
                          <span className="font-semibold">Transcription Failed</span>
                        </div>
                        <p className="text-red-600 text-sm mb-3">{error}</p>
                        <button
                            onClick={handleRetry}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 text-sm"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Retry Transcription
                        </button>
                      </div>
                  )}

                  {!isConverted && !isConverting && !error ? (
                      <button
                          onClick={handleConvert}
                          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 cursor-pointer"
                      >
                        <Sparkles className="w-5 h-5" />
                        Transcribe Audio
                      </button>
                  ) : null}

                  {isConverting && (
                      <div className="text-center py-8">
                        {isUploading ? (
                            <div>
                              <div className="inline-flex items-center gap-3 text-purple-600 mb-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                                <span className="font-semibold">
                          Uploading... {uploadProgress}%
                        </span>
                              </div>
                              <div className="bg-gray-200 rounded-full h-3 overflow-hidden mb-2">
                                <div
                                    className="bg-gradient-to-r from-purple-600 to-indigo-600 h-full rounded-full transition-all duration-300 ease-out"
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                              </div>
                              <p className="text-sm text-gray-600">
                                {uploadProgress < 100 ? "Please wait while we upload your file..." : "Upload complete, starting conversion..."}
                              </p>
                            </div>
                        ) : isProcessing ? (
                            <div>
                              <div className="inline-flex items-center gap-3 text-purple-600 mb-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                                <span className="font-semibold">
                          Processing your audio...
                        </span>
                              </div>
                              <div className="bg-gray-200 rounded-full h-3 overflow-hidden mb-2">
                                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 h-full rounded-full animate-pulse w-full"></div>
                              </div>
                              <p className="text-sm text-gray-600">
                                Transcribing your audio file, this may take a few moments...
                              </p>
                            </div>
                        ) : (
                            <div>
                              <div className="inline-flex items-center gap-3 text-purple-600">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                                <span className="font-semibold">
                          Initializing...
                        </span>
                              </div>
                            </div>
                        )}
                      </div>
                  )}

                  {isConverted && (transcriptionData || transcriptionText) && (
                      <div className="space-y-4">
                        <div className="text-center py-4">
                          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-8 h-8 text-white" />
                          </div>
                          <h4 className="text-xl font-semibold text-gray-800 mb-2">
                            Transcription Complete!
                          </h4>
                          <p className="text-gray-600 mb-4">
                            Your audio has been transcribed to text
                          </p>
                        </div>
                        
                        <div className="bg-white rounded-xl border border-gray-200 p-4 max-h-96 overflow-y-auto">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-semibold text-gray-800">Transcription:</h5>
                            <button
                                onClick={handleCopyText}
                                className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
                            >
                              <Copy className="w-4 h-4" />
                              Copy
                            </button>
                          </div>
                          
                          {transcriptionData && transcriptionData.length > 0 ? (
                            <div className="space-y-3">
                              {transcriptionData.map((segment: any, index: number) => (
                                <div key={segment.uuid || index} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                                  <div className="flex items-start gap-3">
                                    <span className="text-xs font-mono text-purple-600 bg-purple-50 px-2 py-1 rounded whitespace-nowrap mt-1">
                                      {formatTime(segment.time)}
                                    </span>
                                    <p className="text-gray-700 leading-relaxed flex-1">
                                      {segment.title}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                              {transcriptionText}
                            </p>
                          )}
                        </div>

                        <button
                            onClick={handleDownloadText}
                            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                        >
                          <Download className="w-5 h-5" />
                          Download as TXT
                        </button>

                        {/* Upsell Section */}
                        <div className="bg-gradient-to-r from-purple-50 via-indigo-50 to-pink-50 rounded-xl border-2 border-purple-200 p-6 mt-4">
                          <div className="text-center mb-4">
                            <h5 className="text-lg font-bold text-gray-800 mb-2">
                              Unlock More Export Formats
                            </h5>
                            <p className="text-sm text-gray-600">
                              Get access to professional formats perfect for video editing, YouTube, and more
                            </p>
                          </div>
                          
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                            {[
                              { format: "SRT", desc: "YouTube, Vimeo" },
                              { format: "VTT", desc: "Web players" },
                              { format: "PDF", desc: "Documentation" },
                              { format: "ASS", desc: "Advanced editing" },
                            ].map((item) => (
                              <div key={item.format} className="text-center">
                                <div className="bg-white rounded-lg p-3 shadow-sm border border-purple-100">
                                  <div className="font-bold text-purple-600 text-lg mb-1">
                                    {item.format}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {item.desc}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                            <a
                                href="/sign-up?ref=audio-to-text"
                                className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                            >
                              <Sparkles className="w-5 h-5" />
                              Sign Up Free for More Formats
                            </a>
                        
                          </div>
                        </div>
                      </div>
                  )}
                </div>
              </div>
          )}
        </div>

        {/* Trust indicators */}
        <div className="text-center mt-12">
          <p className="text-sm text-gray-500 mb-4">
            Trusted by content creators worldwide
          </p>
          <div className="flex justify-center items-center gap-8 opacity-60">
            <div className="text-2xl font-bold text-gray-400">
              100+ Files Transcribed
            </div>
            <div className="text-2xl font-bold text-gray-400">•</div>
            <div className="text-2xl font-bold text-gray-400">100% Free</div>
          </div>
        </div>
      </div>
  );
};

export default UploadSection;